import { useEffect, useMemo, useRef, useReducer } from "react";
import { api } from "services/api";
import { useGetBackgroundJobByIdQuery } from "services/background-job-api";
import { useDispatch } from "utils/redux";

import { logMetadocDebug } from "../../utils/metadocDebugLogger";

export type MetadocOperationType =
  | "none"
  | "move"
  | "delete"
  | "regenerate"
  | "ai";

export type MetadocOperationPhase =
  | "idle"
  | "starting"
  | "pending"
  | "in_progress"
  | "finalizing"
  | "success"
  | "failed";

export type MetadocOperationMeta = {
  pageCount?: number;
  pageRanges?: number[][];
  targetPosition?: number;
};

export type MetadocOperationState = {
  operationType: MetadocOperationType;
  phase: MetadocOperationPhase;
  jobId: number | null;
  statusMessage: string | null;
  meta: MetadocOperationMeta | null;
  refetchObserved: boolean;
  pdfReloadObserved: boolean;
};

export type MetadocOperationAction =
  | {
      type: "OPERATION_STARTED";
      payload: {
        operationType: Exclude<MetadocOperationType, "none">;
        meta?: MetadocOperationMeta;
      };
    }
  | { type: "JOB_ATTACHED"; payload: { jobId: number } }
  | {
      type: "JOB_STATUS_UPDATED";
      payload: {
        status: string;
        statusMessage?: string | null;
      };
    }
  | { type: "REFETCH_STARTED" }
  | { type: "PDF_RELOAD_OBSERVED" }
  | { type: "OPERATION_FINALIZED" }
  | { type: "OPERATION_FAILED"; payload?: { statusMessage?: string | null } }
  | { type: "RESET" };

export const initialMetadocOperationState: MetadocOperationState = {
  operationType: "none",
  phase: "idle",
  jobId: null,
  statusMessage: null,
  meta: null,
  refetchObserved: false,
  pdfReloadObserved: false,
};

const ACTIVE_PHASES: MetadocOperationPhase[] = [
  "starting",
  "pending",
  "in_progress",
  "finalizing",
];

const formatPageRanges = (pageRanges?: number[][]): string => {
  if (!pageRanges || pageRanges.length === 0) {
    return "";
  }

  return pageRanges
    .map(([start, end]) => (start === end ? `${start}` : `${start}-${end}`))
    .join(", ");
};

const normalizeStatus = (status: string): MetadocOperationPhase | null => {
  const normalized = status.trim().toLowerCase();
  if (normalized === "pending") return "pending";
  if (normalized === "in progress") return "in_progress";
  if (normalized === "success") return "success";
  if (normalized === "failed") return "failed";
  return null;
};

const isActiveState = (phase: MetadocOperationPhase): boolean =>
  ACTIVE_PHASES.includes(phase);

export const metadocOperationReducer = (
  state: MetadocOperationState,
  action: MetadocOperationAction,
): MetadocOperationState => {
  switch (action.type) {
    case "OPERATION_STARTED": {
      if (isActiveState(state.phase)) {
        return state;
      }

      return {
        operationType: action.payload.operationType,
        phase: "starting",
        jobId: null,
        statusMessage: null,
        meta: action.payload.meta || null,
        refetchObserved: false,
        pdfReloadObserved: false,
      };
    }

    case "JOB_ATTACHED": {
      if (state.phase !== "starting") {
        return state;
      }

      return {
        ...state,
        jobId: action.payload.jobId,
        phase: "pending",
      };
    }

    case "JOB_STATUS_UPDATED": {
      if (
        !["pending", "in_progress", "finalizing"].includes(state.phase) ||
        !state.jobId
      ) {
        return state;
      }

      const nextPhase = normalizeStatus(action.payload.status);
      if (!nextPhase) {
        return state;
      }

      if (nextPhase === "pending") {
        return {
          ...state,
          phase: "pending",
          statusMessage: action.payload.statusMessage ?? state.statusMessage,
        };
      }

      if (nextPhase === "in_progress") {
        return {
          ...state,
          phase: "in_progress",
          statusMessage: action.payload.statusMessage ?? state.statusMessage,
        };
      }

      if (nextPhase === "success") {
        return {
          ...state,
          phase: "finalizing",
          statusMessage: action.payload.statusMessage ?? state.statusMessage,
        };
      }

      return {
        ...state,
        phase: "failed",
        statusMessage: action.payload.statusMessage ?? state.statusMessage,
      };
    }

    case "REFETCH_STARTED": {
      if (state.phase !== "finalizing") {
        return state;
      }

      return {
        ...state,
        refetchObserved: true,
      };
    }

    case "PDF_RELOAD_OBSERVED": {
      if (state.phase !== "finalizing") {
        return state;
      }

      return {
        ...state,
        pdfReloadObserved: true,
      };
    }

    case "OPERATION_FINALIZED": {
      if (
        state.phase !== "finalizing" ||
        !state.refetchObserved ||
        !state.pdfReloadObserved
      ) {
        return state;
      }

      return {
        ...state,
        phase: "success",
      };
    }

    case "OPERATION_FAILED": {
      if (state.phase === "idle") {
        return state;
      }

      return {
        ...state,
        phase: "failed",
        statusMessage: action.payload?.statusMessage ?? state.statusMessage,
      };
    }

    case "RESET":
      return initialMetadocOperationState;

    default:
      return state;
  }
};

type UseMetadocOperationControllerParams = {
  metadocId?: number;
  isMetadocFetching?: boolean;
  isPdfLoading?: boolean;
};

type MetadocOperationOverlayModel = {
  title: string;
  icon: string;
  statusText: string;
  showSpinner: boolean;
};

type UseMetadocOperationControllerReturn = {
  state: MetadocOperationState;
  isUiLocked: boolean;
  showOverlay: boolean;
  shouldPollJob: boolean;
  overlayModel: MetadocOperationOverlayModel | null;
  startOperation: (
    operationType: Exclude<MetadocOperationType, "none">,
    meta?: MetadocOperationMeta,
  ) => void;
  attachJob: (jobId: number) => void;
  failOperation: (statusMessage?: string) => void;
  resetOperation: () => void;
};

/**
 * coordinates metadoc operation lifecycle with a reducer state machine.
 * centralizes job polling, ui lock rules, and finalize gating behavior.
 */
export const useMetadocOperationController = ({
  metadocId,
  isMetadocFetching = false,
  isPdfLoading = false,
}: UseMetadocOperationControllerParams): UseMetadocOperationControllerReturn => {
  const dispatch = useDispatch();
  const [state, reducerDispatch] = useReducer(
    metadocOperationReducer,
    initialMetadocOperationState,
  );
  const invalidatedForJobRef = useRef<number | null>(null);
  const wasPollingRef = useRef(false);
  const lastLoggedTerminalStateRef = useRef<string | null>(null);

  const shouldPollJob = !!state.jobId && isActiveState(state.phase);
  const { data: backgroundJobData } = useGetBackgroundJobByIdQuery(
    state.jobId ?? 0,
    { skip: !shouldPollJob, pollingInterval: 3000 },
  );

  useEffect(() => {
    if (shouldPollJob && !wasPollingRef.current && state.jobId) {
      logMetadocDebug("operation controller job polling started", {
        metadocId,
        jobId: state.jobId,
        operationType: state.operationType,
        phase: state.phase,
      });
    }

    if (!shouldPollJob && wasPollingRef.current) {
      logMetadocDebug("operation controller job polling stopped", {
        metadocId,
        jobId: state.jobId,
        operationType: state.operationType,
        phase: state.phase,
      });
    }

    wasPollingRef.current = shouldPollJob;
  }, [shouldPollJob, state.jobId, state.operationType, state.phase, metadocId]);

  useEffect(() => {
    // consume latest job status for the active operation.
    if (!state.jobId || !backgroundJobData) {
      return;
    }

    const normalizedStatus = normalizeStatus(backgroundJobData.status);
    if (
      normalizedStatus &&
      (normalizedStatus === "success" || normalizedStatus === "failed") &&
      lastLoggedTerminalStateRef.current !==
        `${backgroundJobData.id}:${normalizedStatus}`
    ) {
      logMetadocDebug("operation controller job reached terminal status", {
        metadocId,
        jobId: backgroundJobData.id,
        operationType: state.operationType,
        jobStatus: backgroundJobData.status,
        jobStatusMessage: backgroundJobData.status_message ?? null,
      });
      lastLoggedTerminalStateRef.current = `${backgroundJobData.id}:${normalizedStatus}`;
    }

    reducerDispatch({
      type: "JOB_STATUS_UPDATED",
      payload: {
        status: backgroundJobData.status,
        statusMessage: backgroundJobData.status_message,
      },
    });

    if (normalizedStatus !== "success") {
      return;
    }

    // invalidate once per successful job to trigger fresh metadoc + groups fetch.
    if (!metadocId || invalidatedForJobRef.current === backgroundJobData.id) {
      return;
    }

    invalidatedForJobRef.current = backgroundJobData.id;
    logMetadocDebug("operation controller invalidating metadoc caches", {
      metadocId,
      jobId: backgroundJobData.id,
      operationType: state.operationType,
      tags: [
        "Metadocs:id",
        "Metadocs:list",
        "DocumentGroups",
        "DocumentGroupTags",
      ],
    });
    dispatch(
      api.util.invalidateTags([
        { type: "Metadocs", id: metadocId },
        "Metadocs",
        "DocumentGroups",
        "DocumentGroupTags",
      ]),
    );
  }, [
    state.jobId,
    state.operationType,
    state.phase,
    backgroundJobData,
    dispatch,
    metadocId,
  ]);

  useEffect(() => {
    // phase gate 1: refetch of metadoc payload has started.
    if (state.phase === "finalizing" && isMetadocFetching) {
      reducerDispatch({ type: "REFETCH_STARTED" });
    }
  }, [state.phase, isMetadocFetching]);

  useEffect(() => {
    // phase gate 2: refreshed PDF load cycle has started.
    if (state.phase === "finalizing" && isPdfLoading) {
      reducerDispatch({ type: "PDF_RELOAD_OBSERVED" });
    }
  }, [state.phase, isPdfLoading]);

  useEffect(() => {
    // finalize only after both gates have been observed and both are now settled.
    // this prevents UI unlock before the refreshed document is actually ready.
    if (
      state.phase === "finalizing" &&
      state.refetchObserved &&
      state.pdfReloadObserved &&
      !isPdfLoading &&
      !isMetadocFetching
    ) {
      reducerDispatch({ type: "OPERATION_FINALIZED" });
    }
  }, [
    state.phase,
    state.refetchObserved,
    state.pdfReloadObserved,
    isPdfLoading,
    isMetadocFetching,
  ]);

  const overlayModel = useMemo<MetadocOperationOverlayModel | null>(() => {
    if (!isActiveState(state.phase)) {
      return null;
    }

    if (state.operationType === "move") {
      const pageRangesText = formatPageRanges(state.meta?.pageRanges);
      return {
        title:
          `Moving Page${state.meta?.pageCount === 1 ? "" : "s"} ${pageRangesText}${state.meta?.targetPosition ? ` to Position ${state.meta.targetPosition}` : ""}`.trim(),
        icon: "drive_file_move",
        statusText: state.phase === "pending" ? "Pending" : "In Progress",
        showSpinner: true,
      };
    }

    if (state.operationType === "delete") {
      const pageRangesText = formatPageRanges(state.meta?.pageRanges);
      return {
        title:
          `Deleting Page${state.meta?.pageCount === 1 ? "" : "s"} ${pageRangesText}`.trim(),
        icon: "delete",
        statusText: state.phase === "pending" ? "Pending" : "In Progress",
        showSpinner: true,
      };
    }

    if (state.operationType === "regenerate") {
      return {
        title: "Regenerating PDF with Updated Table of Contents",
        icon: "auto_fix_high",
        statusText:
          state.phase === "starting"
            ? "Saving Changes"
            : state.phase === "pending"
              ? "Pending"
              : "In Progress",
        showSpinner: true,
      };
    }

    return {
      title: "Processing",
      icon: "hourglass_empty",
      statusText: state.phase === "pending" ? "Pending" : "In Progress",
      showSpinner: true,
    };
  }, [state.phase, state.operationType, state.meta]);

  const isUiLocked = isActiveState(state.phase);
  const showOverlay = isActiveState(state.phase);

  return {
    state,
    isUiLocked,
    showOverlay,
    shouldPollJob,
    overlayModel,
    startOperation: (operationType, meta) =>
      reducerDispatch({
        type: "OPERATION_STARTED",
        payload: { operationType, meta },
      }),
    attachJob: (jobId) =>
      reducerDispatch({
        type: "JOB_ATTACHED",
        payload: { jobId },
      }),
    failOperation: (statusMessage) =>
      reducerDispatch({
        type: "OPERATION_FAILED",
        payload: { statusMessage },
      }),
    resetOperation: () => {
      invalidatedForJobRef.current = null;
      reducerDispatch({ type: "RESET" });
    },
  };
};
